// Pure rules of the Prime composer: what a key does, fenced code, indentation
// and the size limits on pictures. No DOM here, so all of it is tested.

/** Mirrors agentsession.MaxImages / MaxImageData on the server. */
export const MAX_IMAGES = 4
export const MAX_IMAGE_DATA = 3 << 20
/** Longest side a picture is scaled to before it is sent. */
export const MAX_IMAGE_SIDE = 1568
/** The largest file we will even try to read. */
export const MAX_IMAGE_FILE = 25 << 20
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
export const INDENT = '  '

export interface Attached {
  id: string
  media_type: string
  data: string
  name: string
}

export type KeyAction = 'send' | 'newline' | 'none'

interface KeyLike {
  key: string
  shiftKey: boolean
  metaKey: boolean
  ctrlKey: boolean
  altKey?: boolean
  isComposing?: boolean
}

/**
 * Enter on a phone sends (as before); on a desktop it is a newline and
 * Cmd/Ctrl+Enter sends, unless the host keeps Enter sending (`enterSends`, the
 * side panel, as it was). Cmd/Ctrl+Enter sends everywhere; Shift+Enter is
 * always a newline; a key during IME composition is never ours.
 */
export const keyAction = (e: KeyLike, fine: boolean, enterSends = false): KeyAction => {
  if (e.key !== 'Enter' || e.isComposing) return 'none'
  if (e.metaKey || e.ctrlKey) return 'send'
  if (e.shiftKey || e.altKey) return 'newline'
  return fine && !enterSends ? 'newline' : 'send'
}

/** Whether the caret (an index) is inside an open ``` fence. */
export const inFence = (text: string, pos: number): boolean => {
  let open = false
  let at = 0
  for (const line of text.slice(0, pos).split('\n')) {
    // a fence line only counts once its line is complete or the caret is past its backticks
    if (/^\s*(```|~~~)/.test(line) && at + line.length <= pos) open = !open
    at += line.length + 1
  }
  return open
}

export interface Edit {
  text: string
  start: number
  end: number
}

/**
 * Tab inside a fence: indent. With a selection spanning lines every line is
 * indented (or outdented with shift); a plain caret inserts the indent (or,
 * with shift, removes one before the line's text). Outside a fence: null, so
 * the browser's Tab moves focus as usual.
 */
export const tabEdit = (text: string, start: number, end: number, shift: boolean): Edit | null => {
  if (!inFence(text, start)) return null
  const multi = text.slice(start, end).includes('\n')
  if (!shift && !multi) {
    return {
      text: text.slice(0, start) + INDENT + text.slice(end),
      start: start + INDENT.length,
      end: start + INDENT.length,
    }
  }
  const from = text.lastIndexOf('\n', start - 1) + 1
  const lines = text.slice(from, end).split('\n')
  let delta0 = 0
  let total = 0
  const out = lines.map((line, i) => {
    let next: string
    let d: number
    if (shift) {
      const m = /^( {1,2}|\t)/.exec(line)
      const cut = m ? m[0].length : 0
      next = line.slice(cut)
      d = -cut
    } else {
      next = INDENT + line
      d = INDENT.length
    }
    if (i === 0) delta0 = d
    total += d
    return next
  })
  const nextStart = Math.max(from, start + delta0)
  return {
    text: text.slice(0, from) + out.join('\n') + text.slice(end),
    start: nextStart,
    end: Math.max(nextStart, end + total),
  }
}

/** Enter inside a fence keeps the previous line's indentation. */
export const enterEdit = (text: string, start: number, end: number): Edit | null => {
  if (!inFence(text, start)) return null
  const line = text.slice(text.lastIndexOf('\n', start - 1) + 1, start)
  if (/^\s*(```|~~~)/.test(line)) return null
  const indent = /^[ \t]*/.exec(line)?.[0] ?? ''
  if (!indent) return null
  const ins = '\n' + indent
  return {
    text: text.slice(0, start) + ins + text.slice(end),
    start: start + ins.length,
    end: start + ins.length,
  }
}

/** Scale (w, h) so the longer side is at most max, never up. */
export const fitWithin = (w: number, h: number, max = MAX_IMAGE_SIDE): { w: number; h: number } => {
  const long = Math.max(w, h)
  if (long <= max) return { w, h }
  const k = max / long
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) }
}

/** Why a file cannot be attached, or null. */
export const refuseFile = (file: { type: string; size: number }, have: number): string | null => {
  if (!IMAGE_TYPES.includes(file.type)) return 'Only png, jpeg, gif or webp pictures'
  if (have >= MAX_IMAGES) return `At most ${MAX_IMAGES} pictures`
  if (file.size > MAX_IMAGE_FILE) return 'That picture is too large'
  return null
}

/**
 * The side panel's message with the page's selected text under it. The
 * selection rides whenever there is one, also on a message of pictures alone.
 */
export const withSelection = (text: string, sel: string): string => {
  const quoted = `Selected text:\n"""\n${sel}\n"""`
  if (!sel) return text
  return text ? `${text}\n\n${quoted}` : quoted
}

/** True when the message has something to send. */
export const canSend = (text: string, images: number): boolean => text.trim().length > 0 || images > 0

/** Most lines the box grows to before it scrolls: fewer on a phone while its keyboard is up. */
export const maxLinesFor = (keyboardUp: boolean, fine: boolean): number => (keyboardUp && !fine ? 4 : 8)

/** Fewest lines the box shows: a wide host's roomy box gets one more than a narrow one's. */
export const minLinesFor = (compact: boolean, fine: boolean): number => (!compact && fine ? 3 : 2)

/**
 * The box's height for its content: grows from `min` lines to `max`, then holds and scrolls.
 * `content` is the textarea's scrollHeight, `line` one line's height, `pad` the vertical padding.
 */
export const boxHeight = (
  content: number,
  line: number,
  pad: number,
  min: number,
  max: number,
): { height: number; scrolls: boolean } => {
  const lo = line * min + pad
  const hi = line * max + pad
  return { height: Math.min(hi, Math.max(lo, content)), scrolls: content > hi }
}
